import { useEffect, useState } from "react";
import axios from "axios";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Loader2, RefreshCw, Users, LogOut, KeyRound } from "lucide-react";
import { Link } from "react-router-dom";
import { SEGMENT_LABELS } from "@/components/preregister/preRegisterConstants";
import { useAdminSession } from "@/hooks/useAdminSession";
import { AdminLoginForm } from "@/components/shared/AdminLoginForm";
import { AdminChangePasswordModal } from "@/components/shared/AdminChangePasswordModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CHART_TICK_STYLE = { fontSize: 11 };
const BAR_CHART_MARGIN = { left: 20 };

export default function Insights() {
  const { admin, checking, login, logout } = useAdminSession();
  const [data, setData] = useState(null);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: res }, { data: segRes }] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { params: { days: 30 } }),
        axios.get(`${API}/leads/perks-notify-segments`),
      ]);
      setData(res);
      setSegments(segRes?.segments || []);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (admin) load();
  }, [admin]);

  if (checking) return null;

  if (!admin) {
    return <AdminLoginForm title="Analytics Dashboard" subtitle="Sign in to view site insights." onLogin={login} />;
  }

  if (!data) {
    return (
      <div data-testid="insights-loading" className="min-h-screen bg-rr-surface flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-rr-orange" />
      </div>
    );
  }

  return (
    <div data-testid="insights-dashboard" className="min-h-screen bg-rr-surface py-14 px-5 md:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-rr-navy">Site Insights</h1>
            <p className="text-sm text-rr-muted mt-1">Last 30 days · privacy-first, self-hosted analytics</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setChangePasswordOpen(true)}
              data-testid="insights-change-password-button"
              className="flex items-center gap-2 text-sm font-bold text-rr-navy border border-rr-line rounded-full px-4 py-2 hover:border-rr-orange transition-colors bg-white"
            >
              <KeyRound size={14} /> Change Password
            </button>
            <button
              onClick={load}
              data-testid="insights-refresh-button"
              className="flex items-center gap-2 text-sm font-bold text-rr-navy border border-rr-line rounded-full px-4 py-2 hover:border-rr-orange transition-colors bg-white"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="insights-logout-button"
              className="flex items-center gap-2 text-sm font-bold text-rr-muted border border-rr-line rounded-full px-4 py-2 hover:border-rr-red hover:text-rr-red transition-colors bg-white"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>

        <Link
          to="/admin/leads"
          data-testid="insights-view-waitlist-link"
          className="inline-flex items-center gap-2 text-sm font-bold text-rr-orange hover:text-rr-navy transition-colors mb-8"
        >
          <Users size={15} /> View Full Registration List →
        </Link>

        <div className="grid grid-cols-2 md:grid-cols-2 gap-5 mb-8">
          <div data-testid="insights-stat-pageviews" className="bg-white rounded-2xl border border-rr-line p-6">
            <div className="text-xs font-bold uppercase tracking-wide text-rr-muted">Pageviews</div>
            <div className="font-heading text-3xl font-extrabold text-rr-navy mt-2">{data.total_pageviews}</div>
          </div>
          <div data-testid="insights-stat-sessions" className="bg-white rounded-2xl border border-rr-line p-6">
            <div className="text-xs font-bold uppercase tracking-wide text-rr-muted">Unique Visitors</div>
            <div className="font-heading text-3xl font-extrabold text-rr-navy mt-2">{data.unique_sessions}</div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div data-testid="insights-daily-chart" className="bg-white rounded-2xl border border-rr-line p-6">
            <div className="text-sm font-bold text-rr-navy mb-4">Pageviews Over Time</div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.daily_pageviews}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8EE" />
                <XAxis dataKey="date" tick={CHART_TICK_STYLE} />
                <YAxis tick={CHART_TICK_STYLE} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#F2761E" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div data-testid="insights-sections-chart" className="bg-white rounded-2xl border border-rr-line p-6">
            <div className="text-sm font-bold text-rr-navy mb-4">Most-Viewed Sections</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.top_sections} layout="vertical" margin={BAR_CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8EE" />
                <XAxis type="number" tick={CHART_TICK_STYLE} allowDecimals={false} />
                <YAxis type="category" dataKey="section" tick={CHART_TICK_STYLE} width={110} />
                <Tooltip />
                <Bar dataKey="count" fill="#0E2A47" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div data-testid="insights-top-pages" className="bg-white rounded-2xl border border-rr-line p-6 mt-6">
          <div className="text-sm font-bold text-rr-navy mb-4">Top Pages</div>
          <div className="space-y-2">
            {data.top_pages.map((p) => (
              <div key={p.path} className="flex items-center justify-between text-sm py-2 border-b border-rr-line last:border-0">
                <span className="text-rr-navy font-medium">{p.path}</span>
                <span className="text-rr-muted font-bold">{p.count}</span>
              </div>
            ))}
            {data.top_pages.length === 0 && <p className="text-sm text-rr-muted">No pageview data yet.</p>}
          </div>
        </div>

        <div data-testid="insights-perks-notify-segments" className="bg-white rounded-2xl border border-rr-line p-6 mt-6">
          <div className="text-sm font-bold text-rr-navy mb-1">Perks Notify Opt-Ins by Segment</div>
          <p className="text-xs text-rr-muted mb-4">Who's most excited for the exclusive Founding Ambassador perks reveal.</p>
          <div className="space-y-2">
            {segments.map((s) => (
              <div key={s.segment} className="flex items-center justify-between text-sm py-2 border-b border-rr-line last:border-0">
                <span className="text-rr-navy font-medium">{SEGMENT_LABELS[s.segment] || s.segment}</span>
                <span className="text-rr-muted font-bold">{s.count}</span>
              </div>
            ))}
            {segments.length === 0 && <p className="text-sm text-rr-muted">No perks opt-ins yet.</p>}
          </div>
        </div>
      </div>
      <AdminChangePasswordModal open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
}
